import { Box, Button, Card, CardContent, TextField, Typography } from "@material-ui/core";
import PrintIcon from "@material-ui/icons/Print";
import { ProgressIcon } from "components/ProgressIcon";
import {
	DEFAULT_NUMBER_OF_LABELS,
	MAX_NUMBER_OF_LABELS,
	MIN_NUMBER_OF_LABELS,
	MIN_SSCC_LABEL_ID,
} from "entities/InternalPalletLabels";
import React from "react";
import { Confirm, useTranslate } from "react-admin";
import { downloadFileWithNotification } from "utils/download";
import { useNotifyError } from "utils/notifiers/useNotifyError";

const LABELS_FILENAME = "internal_pallet_labels.pdf";

const INPUT_WIDTH = 220;

const LabelsActionSection = ({ title, hint, children }) => (
	<Box>
		<Typography variant="h6">{title}</Typography>
		<Typography variant="body2" color="textSecondary">
			{hint}
		</Typography>
		<Box display="flex" alignItems="center" gridGap={16} marginTop={2}>
			{children}
		</Box>
	</Box>
);

export const PrintInternalPalletLabels = ({ terminalId, hasLabelLogo }) => {
	const [numberOfLabels, setNumberOfLabels] = React.useState(DEFAULT_NUMBER_OF_LABELS);
	const [ssccLabelId, setSsccLabelId] = React.useState("");
	const [isPrinting, setIsPrinting] = React.useState(false);
	const [isReprinting, setIsReprinting] = React.useState(false);
	const [actionAwaitingConfirm, setActionAwaitingConfirm] = React.useState(null);

	const translate = useTranslate();
	const notifyError = useNotifyError();

	const isNumberOfLabelsOutOfRange =
		numberOfLabels !== "" &&
		(Number(numberOfLabels) < MIN_NUMBER_OF_LABELS || Number(numberOfLabels) > MAX_NUMBER_OF_LABELS);
	const canPrint = numberOfLabels !== "" && !isNumberOfLabelsOutOfRange;
	const canReprint = Number(ssccLabelId) >= MIN_SSCC_LABEL_ID;

	const handleNumberOfLabelsChange = (event) => {
		setNumberOfLabels(event.target.value);
	};

	const handleSsccLabelIdChange = (event) => {
		setSsccLabelId(event.target.value);
	};

	const handlePrint = async () => {
		setIsPrinting(true);
		await downloadFileWithNotification(
			`/terminal/${terminalId}/internal-pallet-labels/print`,
			LABELS_FILENAME,
			notifyError,
			{
				method: "POST",
				headers: new Headers({ Accept: "application/json", "Content-Type": "application/json" }),
				body: JSON.stringify({ number_of_labels: Number(numberOfLabels) }),
			}
		);
		setIsPrinting(false);
	};

	const handleReprint = async () => {
		setIsReprinting(true);
		await downloadFileWithNotification(
			`/terminal/${terminalId}/internal-pallet-labels/${Number(ssccLabelId)}/label-export.pdf`,
			`internal_pallet_label_${Number(ssccLabelId)}.pdf`,
			notifyError
		);
		setIsReprinting(false);
	};

	const printActions = { print: handlePrint, reprint: handleReprint };

	const startPrinting = (action) => {
		if (hasLabelLogo) {
			printActions[action]();
			return;
		}
		setActionAwaitingConfirm(action);
	};

	const handleConfirm = () => {
		const action = actionAwaitingConfirm;
		setActionAwaitingConfirm(null);
		printActions[action]();
	};

	return (
		<Card>
			<CardContent>
				<LabelsActionSection
					title={translate("resources.terminal.internal-pallet-labels.print_section")}
					hint={translate("resources.terminal.internal-pallet-labels.print_hint")}
				>
					<TextField
						type="number"
						variant="filled"
						size="small"
						style={{ width: INPUT_WIDTH }}
						label={translate("resources.terminal.internal-pallet-labels.number_of_labels")}
						value={numberOfLabels}
						onChange={handleNumberOfLabelsChange}
						inputProps={{ min: MIN_NUMBER_OF_LABELS, max: MAX_NUMBER_OF_LABELS }}
						error={isNumberOfLabelsOutOfRange}
						helperText={
							isNumberOfLabelsOutOfRange
								? translate("resources.terminal.internal-pallet-labels.number_of_labels_range", {
										min: MIN_NUMBER_OF_LABELS,
										max: MAX_NUMBER_OF_LABELS,
								  })
								: ""
						}
					/>
					<Button
						variant="contained"
						color="primary"
						disabled={isPrinting || !canPrint}
						startIcon={isPrinting ? <ProgressIcon /> : <PrintIcon />}
						onClick={() => startPrinting("print")}
					>
						{translate("resources.terminal.internal-pallet-labels.print")}
					</Button>
				</LabelsActionSection>

				<Box marginTop={4}>
					<LabelsActionSection
						title={translate("resources.terminal.internal-pallet-labels.reprint_section")}
						hint={translate("resources.terminal.internal-pallet-labels.reprint_hint")}
					>
						<TextField
							type="number"
							variant="filled"
							size="small"
							style={{ width: INPUT_WIDTH }}
							label={translate("resources.terminal.internal-pallet-labels.sscc_label_id")}
							value={ssccLabelId}
							onChange={handleSsccLabelIdChange}
							inputProps={{ min: MIN_SSCC_LABEL_ID }}
						/>
						<Button
							variant="outlined"
							color="primary"
							disabled={isReprinting || !canReprint}
							startIcon={isReprinting ? <ProgressIcon /> : <PrintIcon />}
							onClick={() => startPrinting("reprint")}
						>
							{translate("resources.terminal.internal-pallet-labels.reprint")}
						</Button>
					</LabelsActionSection>
				</Box>
			</CardContent>
			<Confirm
				isOpen={actionAwaitingConfirm !== null}
				title="resources.terminal.internal-pallet-labels.print_without_logo_title"
				content="resources.terminal.internal-pallet-labels.print_without_logo_content"
				onConfirm={handleConfirm}
				onClose={() => setActionAwaitingConfirm(null)}
			/>
		</Card>
	);
};
